import {
  useLazyGetLoanByContractNumberQuery,
  useLazyGetLoanPenaltiesQuery
} from "@/modules/loan/services";
import type {
  ILoanRepaymentTemplateForm,
  ILoanRepaymentTemplateRequest
} from "@/modules/template/common/types";
import {
  useCreateLoanRepaymentTemplateMutation,
  useGetLoanRepaymentTemplateQuery,
  useUpdateLoanRepaymentTemplateMutation
} from "@/modules/template/services";
import {
  LoanRepaymentKeys,
  LoanRepaymentStatus,
  LoanRepaymentType,
  TransferKeys
} from "@/modules/transaction/common/enum";
import { AmountInfo } from "@/modules/transaction/components/LoanRepayment/AmountInfo";
import { LoanSelectOption } from "@/modules/transaction/components/LoanRepayment/LoanSelectOption";
import { useLoanRepayment } from "@/modules/transaction/hooks";
import { CURRENCY, } from "@/shared/common/constants";
import { CloudflareAsset, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  Amount,
  CloudFlareImage,
  OwnAccountSelectOption,
  Select,
  TemplateLayout
} from "@/shared/components";
import { getAmountOrNull, isValidAmount } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
}

export const LoanRepaymentTemplateForm = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const idempotencyKey = useRef(uuid4()).current;
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const templateName = searchParams.get(SearchParamKeys.TemplateName);
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [getLoan, { data: loan }] = useLazyGetLoanByContractNumberQuery();
  const [getLoanPenalties, { data: penalties }] = useLazyGetLoanPenaltiesQuery();

  const {
    data: loanRepaymentTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetLoanRepaymentTemplateQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });

  const {
    reset,
    values,
    setValue,
    loanOptions,
    accountsList,
    loanContractNumber,
    accountsFilteredList,
    loanRepaymentTypeOptions,
  } = useLoanRepayment({
    isEdit,
    getLoan,
    getLoanPenalties,
    isTemplate: true,
    currency: loan?.currency,
    savedAmount: loanRepaymentTemplate?.amount,
  });

  const [
    createLoanRepaymentTemplateApi,
    { isLoading: isCreateTemplateLoading }
  ] = useCreateLoanRepaymentTemplateMutation();
  const [
    updateLoanRepaymentTemplateApi,
    { isLoading: isUpdateTemplateLoading }
  ] = useUpdateLoanRepaymentTemplateMutation();

  const loanRepaymentTemplateAction = useCallback((values: ILoanRepaymentTemplateForm) => {
    const requestData: ILoanRepaymentTemplateRequest = {
      ...values,
      name: templateName ?? "",
      amount: values.amount || null,
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount
      ),
    };
    return isEdit ?
      updateLoanRepaymentTemplateApi({
        data: requestData,
        id: `${operationId}`
      }).unwrap() :
      createLoanRepaymentTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList]);

  const setDefaultValues = () => {
    const data = {
      loanContractNumber: loanRepaymentTemplate?.loanContractNumber,
      loanRepaymentType: loanRepaymentTemplate?.loanRepaymentType,
      debitAccount: loanRepaymentTemplate?.debitAccount.number,
      amount: getAmountOrNull(loanRepaymentTemplate?.amount),
      purpose: loanRepaymentTemplate?.purpose,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    loanContractNumber: values.loanContractNumber,
    loanRepaymentType: values.loanRepaymentType,
    debitAccount: values.debitAccount,
    amount: getAmountOrNull(values.amount),
    purpose: values.purpose,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (isEdit && !!loanRepaymentTemplate?.transactionType && loanOptions.length) {
      setDefaultValues();
    }
  }, [isEdit, loanRepaymentTemplate, loanOptions]);

  useEffect(() => {
    if (!isEdit) {
      setValue(LoanRepaymentKeys.LoanRepaymentType, LoanRepaymentType.RegularRepayment);
      setValue(TransferKeys.Purpose, t("transfer.loanRepayment.purpose"));
    }
  }, [isEdit]);

  return (
    <TemplateLayout
      title={t("template.loanRepayment.title")}
      page="Loan repayment template"
      accountsList={accountsList}
      isLoading={isCreateTemplateLoading || isUpdateTemplateLoading}
      isGetTemplateLoading={isGetTemplateLoading}
      isEdit={isEdit}
      hasChanges={hasChanges}
      withCommission={false}
      saveTemplate={loanRepaymentTemplateAction}
    >
      <>
        <CollapseGroup
          className="collapse-group"
          items={[
            {
              content: <>
                <FormField
                  name="loanContractNumber"
                  className="mb-32"
                  dataId="loanContractNumber"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      isDisabled={!!loanContractNumber}
                      options={loanOptions}
                      className="custom-select"
                      setSelectedValue={() => {
                        setValue(TransferKeys.DebitAccount, "");
                        setValue(TransferKeys.Amount, "");
                      }}
                      label={t("transfer.loanRepayment.repayableLoan")}
                      placeholder={t("transfer.loanRepayment.chooseLoan")}
                      components={{ Option: LoanSelectOption }}
                    />
                  )}
                />
                {loan && values.loanContractNumber && (
                  <div className="flexbox align-items--center loan-info p-12 mb-32">
                    <div
                      className={classNames(
                        `loan-info--icon flexbox align-items--center justify-content--center`,
                        {
                          "loan-info--icon-danger": loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue
                        }
                      )}>
                      <CloudFlareImage
                        size="small"
                        path={
                          loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue ?
                            CloudflareAsset.LoanDangerLight :
                            CloudflareAsset.Loan
                        }
                      />
                    </div>
                    <div className="pl-12">
                      <Text weight="semibold">{t(loan.loanType)}</Text>
                      <div className="flexbox">
                        <Text type="tertiary">
                          {loan.contractNumber} • {loan.interestRate}%
                        </Text>
                        {loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue && (
                          <>
                            <Text className="px-4" type="tertiary">•</Text>
                            <Text type="danger-light">
                              {t("transfer.loanRepayment.overdue")}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <FormField
                  name="loanRepaymentType"
                  className="mb-32"
                  dataId="loanRepaymentType"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      options={loanRepaymentTypeOptions}
                      label={t("transfer.loanRepayment.loanRepaymentType")}
                      placeholder={t("transfer.loanRepayment.chooseLoanRepaymentType")}
                    />
                  )}
                />
                <FormField
                  name="debitAccount"
                  className="mb-32"
                  dataId="debitAccount"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      className="custom-select"
                      noOptionsMessage={() => {
                        return values.loanContructNumber ?
                          t("transfer.loanRepayment.haventAccessOrEmpty") :
                          t("transfer.loanRepayment.emptyDebitAccountList");
                      }}
                      options={accountsFilteredList || []}
                      label={t("transfer.debitAccount")}
                      placeholder={t("transfer.chooseDebitAccount")}
                      components={{ Option: OwnAccountSelectOption }}
                    />
                  )}
                />
                {values.loanRepaymentType === LoanRepaymentType.FullRepayment && loan ? (
                  <div>
                    <Text weight="semibold" className="mb-10">
                      {t("general.amount")}
                    </Text>
                    <Amount
                      position="left"
                      weight="semibold"
                      size="medium"
                      currency={loan.currency}
                      amount={loan.upcomingPaymentAmount || 0}
                    />
                  </div>
                ) : (
                  <FormField
                    name="amount"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        type="numeric"
                        allowNegative={false}
                        allowLeadingZeros={false}
                        label={t("general.amount")}
                        placeholder={t("general.enterAmount")}
                        thousandSeparator=","
                        decimalScale={loan?.currency === CURRENCY.AMD.label ? 1 : 2}
                        rightIconProps={{
                          Component: loan?.currency ? CURRENCY[loan?.currency]?.symbolIcon : "",
                        }}
                        isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                      />
                    )}
                  />
                )}
                {!!values.loanContractNumber && (
                  <AmountInfo
                    loan={loan}
                    penalties={penalties}
                    contractNumber={values.loanContractNumber}
                    loanRepaymentType={values.loanRepaymentType}
                  />
                )}
              </>,
              title: t("transfer.transferDetails"),
              value: "transfer_details",
              isOpen: true,
            },
          ]}
        />
      </>
    </TemplateLayout>
  );
};
